import { useCallback, useEffect, useRef, useState } from 'react'

import { ProposalNode, useAllProposalsQuery } from '@/apollo/graphql'
import { getFetchMoreStatus } from '@/utils'
import { IPageInfo } from '@/types'

const pageSize = 15

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

export const useSelect = (customerId?: number) => {
  const prevKeyword = useRef<string>('')
  const [search, setSearch] = useState<string>('')
  const [proposals, setProposals] = useState<Partial<ProposalNode>[]>([])
  const [pageInfo, setPageInfo] = useState<IPageInfo>(initialPageInfo)

  const { loading, networkStatus } = useAllProposalsQuery({
    fetchPolicy: 'network-only',
    variables: {
      offset: pageInfo?.offset || 0,
      first: pageSize,
      text: search,
      customer: customerId,
    },
    onCompleted: res => {
      if (res.allProposals?.edges) {
        const _proposals: Partial<ProposalNode>[] = []
        res.allProposals.edges.forEach(e => {
          // @ts-ignore
          if (e?.node) _proposals.push(e.node)
        })

        setProposals(prev => [...prev, ..._proposals])
        setPageInfo(prev => ({
          ...prev,
          hasNextPage: res.allProposals?.pageInfo.hasNextPage,
        }))
      }
    },
  })

  useEffect(() => {
    setPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [networkStatus])

  const onFetchMore = useCallback(() => {
    setPageInfo(prev => ({ ...prev, offset: proposals.length }))
  }, [proposals?.length])

  const onSearch = useCallback((text: string) => {
    if (prevKeyword.current !== text) {
      setProposals([])
      prevKeyword.current = text
    }
    setSearch(text)
    setPageInfo(initialPageInfo)
  }, [])

  return {
    loading,
    onSearch,
    pageInfo,
    onFetchMore,
    data: proposals,
  }
}
